"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

const Preloader = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    try {
      setIsLoading(sessionStorage.getItem("preloaderShown") !== "true");
    } catch {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoading) return;

    const timer = setTimeout(() => {
      setIsLoading(false);
      try {
        sessionStorage.setItem("preloaderShown", "true");
      } catch {}
    }, 2500); // Slightly extended to appreciate the premium sequencing

    return () => clearTimeout(timer);
  }, [isLoading]);

  return (
    <AnimatePresence mode="wait">
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } 
          }}
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden font-sans"
        >
          {/* Futuristic Ambient Glow Background */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(var(--primary-rgb),0.12)_0%,transparent_65%)]" />
          
          {/* Subtle Cyber Grid Lines */}
          <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,#808080_1px,transparent_1px),linear-gradient(to_bottom,#808080_1px,transparent_1px)] bg-[size:40px_40px]" />

          {/* Cinematic Split Mask Panels (Exits outward) */}
          <motion.div 
            exit={{ x: "-100%" }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="absolute inset-y-0 left-0 w-1/2 bg-[#0a0a0a] border-r border-white/5"
          />
          <motion.div 
            exit={{ x: "100%" }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="absolute inset-y-0 right-0 w-1/2 bg-[#0a0a0a] border-l border-white/5"
          />

          {/* Central Container */}
          <motion.div 
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 flex flex-col items-center"
          >
            {/* Geometric Tech Frame */}
            <div className="relative w-32 h-32 flex items-center justify-center mb-10">
              {/* Spinning Outer Tech Diamond */}
              <motion.div
                animate={{ rotate: 45 }}
                className="absolute inset-0 border border-primary/20 rounded-sm"
              />
              <motion.div
                animate={{ rotate: -45 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                className="absolute inset-2 border border-dashed border-primary/40 rounded-sm"
              />
              
              {/* Pulsing Energy Core */}
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-20 h-20 bg-gradient-to-tr from-primary to-primary/80 rounded-sm rotate-45 flex items-center justify-center shadow-[0_0_50px_rgba(var(--primary-rgb),0.3)]"
              >
                {/* SVG icon remains upright despite container rotation */}
                <Image
                  src="/favicon.svg"
                  alt="Kasai Equipment icon"
                  width={48}
                  height={48}
                  className="w-12 h-12"
                />
              </motion.div>
            </div>

            {/* Typography Matrix */}
            <div className="text-center overflow-hidden">
              <motion.h1
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1], delay: 0.2 }}
                className="text-6xl font-black text-white tracking-[0.25em] pl-[0.25em] leading-none uppercase"
              >
                Kasai
              </motion.h1>
            </div>

            <motion.p
              initial={{ opacity: 0, letterSpacing: "0.1em" }}
              animate={{ opacity: 0.4, letterSpacing: "0.5em" }}
              transition={{ duration: 1, delay: 0.4 }}
              className="text-xs text-white uppercase mt-4 font-light transition-all pl-[0.5em]"
            >
              Industrial Excellence
            </motion.p>

            {/* Premium Linear Progress Tracking Bar */}
            <div className="w-48 h-[2px] bg-white/10 mt-10 rounded-full overflow-hidden relative">
              <motion.div
                initial={{ left: "-100%" }}
                animate={{ left: "100%" }}
                transition={{
                  duration: 1.8,
                  repeat: Infinity,
                  ease: [0.4, 0, 0.2, 1],
                }}
                className="absolute top-0 bottom-0 w-1/2 bg-gradient-to-r from-transparent via-primary to-transparent"
              />
            </div>
          </motion.div>

          {/* Minimal Corner Technical Specs */}
          <div className="absolute bottom-6 left-8 hidden md:block text-sm font-mono text-white/20 uppercase tracking-widest">
            SYS.INIT // KASAI_OS_V2.0.6
          </div>
          <div className="absolute bottom-6 right-8 hidden md:block text-sm font-mono text-white/20 uppercase tracking-widest">
            © {new Date().getFullYear()} ALL RIGHTS RESERVED
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
